import { inject, injectable } from "inversify";
import type { IService } from "../../entities/Service";
import { DomElementsService } from "../DomElementsService";

@injectable()
class CursorService implements IService {
	public readonly serviceName = "👈 CursorService";

	#element: HTMLDivElement | null = null;

	#isVisible = false;
	public get isVisible() {
		return this.#isVisible;
	}

	constructor(@inject(DomElementsService) private domElements: DomElementsService) {}

	public initialize(): void {
		this.#element = this.createCrosshairElement();
		this.domElements.containerElement.appendChild(this.#element);
	}

	public dispose(): void {
		this.#element?.remove();
		this.#element = null;
		this.#isVisible = false;
	}

	public show(): void {
		this.#isVisible = true;
	}

	public hide(): void {
		this.#isVisible = false;

		if (this.#element) {
			this.#element.style.display = "none";
		}
	}

	public setScreenPosition(x: number, y: number): void {
		if (this.#element === null) {
			console.warn("Cursor element not initialized");
			return;
		}

		if (this.#isVisible === false) {
			return;
		}

		this.#element.style.transform = `translate(${x - 10.5}px, ${y - 10.5}px)`;
		this.#element.style.display = "block";
	}

	private createCrosshairElement(): HTMLDivElement {
		const element = document.createElement("div");

		Object.assign(element.style, {
			position: "absolute",
			left: "0",
			top: "0",
			width: "21px",
			height: "21px",
			pointerEvents: "none",
			willChange: "transform",
			display: "none",
			zIndex: "1000",
		} satisfies Partial<CSSStyleDeclaration>);

		element.innerHTML = `
			<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
				<line x1="10.5" y1="0" x2="10.5" y2="8" stroke="#999999" stroke-width="1"/>
				<line x1="10.5" y1="13" x2="10.5" y2="21" stroke="#999999" stroke-width="1"/>
				<line x1="0" y1="10.5" x2="8" y2="10.5" stroke="#999999" stroke-width="1"/>
				<line x1="13" y1="10.5" x2="21" y2="10.5" stroke="#999999" stroke-width="1"/>
			</svg>
		`;

		return element;
	}
}

export { CursorService };
